"use client";

import { useEffect, useState } from "react";

interface SessionUser {
	id: string;
	name?: string;
	email?: string;
	image?: string;
	provider?: string;
	providerAccountId?: string;
	role?: string;
	permissions?: string[];
	defaultProfileType?: string; // 'youtube', 'twitch' - tracks first login provider
}

interface Session {
	user: SessionUser;
	expires: string;
}

export function useCustomSession() {
	const [session, setSession] = useState<Session | null>(null);
	const [loading, setLoading] = useState(true);
	const [status, setStatus] = useState<"loading" | "authenticated" | "unauthenticated">("loading");

	const fetchSession = async () => {
		try {
			console.log("🔍 Fetching session...");
			const response = await fetch("/api/auth/session", {
				credentials: "include",
			});

			if (response.ok) {
				const sessionData = await response.json();
				console.log("🔍 Session response:", sessionData);
				// API returns 200 + `null` when there is no session (not an error).
				if (sessionData && typeof sessionData === "object" && sessionData.user) {
					setSession(sessionData);
					setStatus("authenticated");
				} else {
					setSession(null);
					setStatus("unauthenticated");
				}
			} else {
				console.log("🔍 Session response not OK:", response.status);
				setSession(null);
				setStatus("unauthenticated");
			}
		} catch (error) {
			console.error("Failed to fetch session:", error);
			setSession(null);
			setStatus("unauthenticated");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchSession();
	}, []);

	const signOut = async () => {
		try {
			const response = await fetch("/api/auth/signout", {
				method: "POST",
				credentials: "include",
			});

			// Always clear session state regardless of response
			setSession(null);

			if (response.ok) {
				// Force a hard redirect to clear any client-side state
				window.location.href = "/";
			} else {
				console.error("Signout failed:", response.status);
				// Still redirect even if request fails
				window.location.href = "/";
			}
		} catch (error) {
			console.error("Failed to sign out:", error);
			// Still clear session and redirect even if request fails
			setSession(null);
			window.location.href = "/";
		}
	};

	return { session, loading, status, signOut, refetch: fetchSession };
}

export type { Session, SessionUser };
